import { useAuth } from "@clerk/remix";
import {
  Card,
  CardHeader,
  cn,
  Image,
  ScrollShadow,
  Spinner,
} from "@heroui/react";
import { Logger } from "@remix-pwa/sw";
import type { LoaderFunction, MetaFunction } from "@remix-run/cloudflare";
import {
  Link,
  useLoaderData,
  useNavigate,
  useNavigation,
} from "@remix-run/react";
import { createClient } from "@supabase/supabase-js";
import { motion } from "framer-motion";
import { useEffect } from "react";
import {
  FaArrowLeft,
  FaFire,
  FaFlagCheckered,
  FaSkull,
  FaUserAstronaut,
} from "react-icons/fa";
import { FaArrowsTurnToDots } from "react-icons/fa6";

import { PrimaryButton } from "~/components/PrimaryButton";
import { DbScenarioType } from "~/types/scenarios";
import { Database } from "~/types/supabase";
import { setSignupSource, track } from "~/utils/analytics";
import { jsonLd, ogImageMeta } from "~/utils/seo";

const logger = new Logger({ prefix: "[/s/$slug]" });

type ScenarioRow = Pick<
  DbScenarioType,
  | "id"
  | "name"
  | "slug"
  | "context"
  | "target_persona"
  | "difficulty"
  | "image_url"
  | "image_position"
  | "is_long_story"
  | "is_pro_only"
  | "objectives"
  | "turn_limit"
  | "play_count"
  | "creator_handle"
  | "owned_by"
  | "created_at"
>;

type RelatedScenario = Pick<
  DbScenarioType,
  | "id"
  | "name"
  | "slug"
  | "image_url"
  | "image_position"
  | "difficulty"
  | "play_count"
  | "creator_handle"
  | "owned_by"
>;

type LoaderData = {
  scenario: ScenarioRow;
  related: RelatedScenario[];
  origin: string;
};

const truncate = (s: string | null | undefined, max: number): string => {
  if (!s) return "";
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) {
    return [{ title: "Scenario — ManUp" }];
  }
  const { scenario, origin } = data as LoaderData;
  const objective = scenario.objectives?.[0] ?? "";
  const title = `${scenario.name} — Practice this dating scenario | ManUp`;
  const description = truncate(
    objective
      ? `${objective}. ${scenario.context}`
      : scenario.context,
    180
  );
  const url = `${origin}/s/${scenario.slug ?? scenario.id}`;
  const creator = scenario.creator_handle
    ? `@${scenario.creator_handle}`
    : "ManUp";

  return [
    { title },
    { name: "description", content: description },
    { property: "og:type", content: "article" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    ...ogImageMeta(scenario.image_url),
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { tagName: "link", rel: "canonical", href: url },
    jsonLd({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: title,
      description,
      image: scenario.image_url,
      url,
      author: {
        "@type": scenario.creator_handle ? "Person" : "Organization",
        name: creator,
      },
      publisher: {
        "@type": "Organization",
        name: "ManUp",
        logo: {
          "@type": "ImageObject",
          url: "https://cdn.manupgame.com/icon.svg",
        },
      },
      datePublished: scenario.created_at ?? undefined,
      keywords: [
        "dating advice",
        "shit test",
        "comeback",
        scenario.difficulty,
        scenario.name,
      ].join(", "),
    }),
    // HowTo schema gives this page a shot at rich results when someone
    // asks "how do I respond when she says X" via Google AI Overviews,
    // ChatGPT search, or Perplexity. The three steps mirror the in-app
    // scenario briefing.
    jsonLd({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: `How to handle: ${scenario.name}`,
      description,
      image: scenario.image_url,
      totalTime: `PT${(scenario.turn_limit ?? 10) * 30}S`,
      step: [
        {
          "@type": "HowToStep",
          position: 1,
          name: "Read the situation",
          text: scenario.context,
        },
        {
          "@type": "HowToStep",
          position: 2,
          name: "Know who you're talking to",
          text: scenario.target_persona,
        },
        {
          "@type": "HowToStep",
          position: 3,
          name: "Hit the objective",
          text: `Within ${scenario.turn_limit ?? 10} turns: ${objective}`,
        },
      ],
    }),
  ];
};

export const loader: LoaderFunction = async (args) => {
  try {
    const slug = args.params.slug;
    if (!slug) {
      throw new Response("Not Found", { status: 404 });
    }

    const supabaseUrl = args.context.cloudflare.env.SUPABASE_URL;
    const supabaseKey = args.context.cloudflare.env.SUPABASE_KEY;
    if (!supabaseUrl || !supabaseKey) {
      throw new Error("Missing Supabase env vars");
    }
    const supabase = createClient<Database>(supabaseUrl, supabaseKey);

    const { data, error } = await supabase
      .from("scenarios")
      .select(
        "id, name, slug, context, target_persona, difficulty, image_url, image_position, is_long_story, is_pro_only, objectives, turn_limit, play_count, creator_handle, owned_by, created_at"
      )
      .eq("slug", slug)
      .eq("is_public", true)
      .maybeSingle();

    if (error) {
      logger.error("Error fetching public scenario", error);
      throw new Response("Not Found", { status: 404 });
    }
    if (!data) {
      throw new Response("Not Found", { status: 404 });
    }

    // Pull a handful of related public scenarios — same difficulty first,
    // ordered by play_count. This gives Googlebot internal links into the
    // marketplace and keeps users one click from another scenario.
    const { data: related } = await supabase
      .from("scenarios")
      .select(
        "id, name, slug, image_url, image_position, difficulty, play_count, creator_handle, owned_by"
      )
      .eq("is_public", true)
      .eq("difficulty", data.difficulty)
      .neq("id", data.id)
      .order("play_count", { ascending: false })
      .limit(4);

    const url = new URL(args.request.url);
    return {
      scenario: data,
      related: (related ?? []) as RelatedScenario[],
      origin: url.origin,
    };
  } catch (error) {
    if (error instanceof Response) throw error;
    console.error(error);
    throw new Response("Internal Server Error", { status: 500 });
  }
};

export default function PublicScenarioPage() {
  const { scenario, related } = useLoaderData<LoaderData>();
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const navigation = useNavigation();

  const playHref = `/scenarios/${scenario.id}`;
  // Logged-out users go through /onboard first so we capture personalization
  // signals (skill/motivation/age) before signup, then land them on the scenario.
  const onboardHref = `/onboard?redirect_url=${encodeURIComponent(playHref)}`;
  const isLaunching =
    navigation.state === "loading" &&
    (navigation.location?.pathname === playHref ||
      navigation.location?.pathname === "/onboard");

  // Page-view event with rich context. We rely on isLoaded to know whether
  // logged_in is accurate; otherwise we'd report stale state.
  useEffect(() => {
    if (!isLoaded) return;
    track("public_scenario_viewed", {
      scenario_id: scenario.id,
      slug: scenario.slug ?? "",
      creator_handle: scenario.creator_handle,
      play_count: scenario.play_count ?? 0,
      logged_in: !!isSignedIn,
    });
  }, [
    isLoaded,
    isSignedIn,
    scenario.id,
    scenario.slug,
    scenario.creator_handle,
    scenario.play_count,
  ]);

  const handlePlay = () => {
    if (!isLoaded) return;
    track("public_scenario_play_clicked", {
      scenario_id: scenario.id,
      slug: scenario.slug ?? "",
      logged_in: !!isSignedIn,
    });
    if (!isSignedIn) {
      // Tag the anonymous user so PostHog Person carries the source forward
      // through onboard → sign-up → identify().
      setSignupSource("public_scenario", {
        scenario_id: scenario.id,
        scenario_slug: scenario.slug,
        creator_handle: scenario.creator_handle,
      });
    }
    navigate(isSignedIn ? playHref : onboardHref);
  };

  const objective = scenario.objectives?.[0] ?? "Reach your goal";
  const creatorLabel = scenario.creator_handle
    ? `@${scenario.creator_handle}`
    : scenario.owned_by
    ? "@anonymous"
    : "ManUp";

  return (
    <div className="flex flex-col min-h-dvh w-full">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="relative w-full"
      >
        <Card className="rounded-none">
          <CardHeader className="absolute z-10 flex-row h-full items-start justify-between p-4 from-transparent via-black/30 to-black/80 bg-gradient-to-b">
            <button
              onClick={() => navigate(-1)}
              aria-label="Back"
              className="p-2 -ml-1 -mt-1 active:opacity-60"
            >
              <FaArrowLeft className="text-white text-lg drop-shadow-md" />
            </button>
            <div className="px-2 py-0.5 bg-white text-black rounded-md text-xs font-bangers flex items-center gap-1 border-1 border-black">
              <FaSkull /> {scenario.difficulty}
            </div>
          </CardHeader>

          <Image
            src={scenario.image_url}
            alt={`Cover image for the dating scenario "${scenario.name}" — ${truncate(scenario.context, 80)}`}
            height={220}
            width={9999}
            className={cn(
              "object-cover rounded-none w-full relative z-0",
              scenario.image_position
            )}
          />
        </Card>

        <div className="absolute bottom-0 left-0 right-0 z-20 px-5 pb-4 font-bangers">
          <h1 className="text-white text-4xl leading-none drop-shadow-[2px_2px_0.1px_rgba(0,0,0,1)]">
            {scenario.name}
          </h1>
          <div className="mt-2 flex flex-row items-center gap-2 text-sm text-white/90 font-mono">
            <span className="inline-flex items-center gap-1.5">
              <FaUserAstronaut className="opacity-80" />
              <span>by {creatorLabel}</span>
            </span>
            {(scenario.play_count ?? 0) > 0 && (
              <>
                <span className="opacity-50">·</span>
                <span className="inline-flex items-center gap-1">
                  <FaFire className="opacity-80" />
                  {formatPlayCount(scenario.play_count ?? 0)} plays
                </span>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Body */}
      <ScrollShadow className="flex-1 px-5 pt-6 pb-[max(7rem,calc(env(safe-area-inset-bottom)+6.5rem))] font-bangers">
        <Section title="Objective">
          <div className="font-mono text-sm font-bold flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <FaArrowsTurnToDots className="shrink-0" />
              <span>Within {scenario.turn_limit ?? 10} turns</span>
            </div>
            <div className="flex items-start gap-2">
              <FaFlagCheckered className="shrink-0 mt-0.5" />
              <span>{objective}</span>
            </div>
          </div>
        </Section>

        <Section title="Her Persona" className="mt-8">
          <p className="font-mono leading-snug tracking-tight text-sm">
            {truncate(scenario.target_persona, 280)}
          </p>
        </Section>

        <Section title="The Setup" className="mt-8">
          <p className="font-mono leading-snug tracking-tight text-sm">
            {scenario.context}
          </p>
        </Section>

        <SocialProofRow scenario={scenario} />

        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="opacity-70 text-xl">More like this</h2>
            <p className="text-xs font-mono opacity-50 mt-1 leading-snug">
              Other {scenario.difficulty} scenarios from the community.
            </p>
            <div className="mt-3 flex flex-col gap-3">
              {related.map((s) => (
                <RelatedScenarioCard key={s.id} scenario={s} />
              ))}
            </div>
          </section>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/scenarios/explore"
            className="inline-flex items-center gap-2 font-bangers text-sm text-primary active:opacity-60"
          >
            Browse all scenarios →
          </Link>
        </div>
      </ScrollShadow>

      {/* Sticky bottom CTA */}
      <div className="fixed z-30 bottom-0 left-0 right-0 flex justify-center pointer-events-none">
        <div className="w-full max-w-2xl px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 bg-gradient-to-t from-neutral-950 via-neutral-950/95 to-transparent pointer-events-auto">
          <div className="flex flex-col items-center">
            <PrimaryButton
              onClick={handlePlay}
              baseClassName="w-full max-w-xs"
              disabled={!isLoaded || isLaunching}
            >
              {isLaunching ? (
                <Spinner variant="wave" color="white" className="m-0 gap-0" />
              ) : isSignedIn ? (
                <>Play This Scenario</>
              ) : (
                <>Sign Up to Play — Free</>
              )}
            </PrimaryButton>
            {!isSignedIn && isLoaded && (
              <Link
                to="/sign-in"
                onClick={() =>
                  track("public_scenario_sign_in_clicked", {
                    scenario_id: scenario.id,
                    slug: scenario.slug ?? "",
                  })
                }
                className="mt-2 opacity-70 text-sm font-mono"
              >
                Already have an account? Log in
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={className}>
    <h2 className="opacity-70 text-xl">{title}</h2>
    <div className="mt-1">{children}</div>
  </div>
);

const SocialProofRow = ({
  scenario,
}: {
  scenario: LoaderData["scenario"];
}) => {
  if ((scenario.play_count ?? 0) === 0) return null;
  return (
    <div className="mt-10 rounded-2xl border-1 border-neutral-800 bg-neutral-900/40 p-4">
      <p className="font-bangers text-sm opacity-70">Community status</p>
      <p className="font-bangers text-3xl mt-1 leading-none">
        {formatPlayCount(scenario.play_count ?? 0)}{" "}
        <span className="text-base opacity-70">guys have tried this</span>
      </p>
      <p className="text-xs font-mono opacity-60 mt-2 leading-snug">
        Think you can handle it better? Step in.
      </p>
    </div>
  );
};

const formatPlayCount = (n: number): string => {
  if (n < 1000) return n.toString();
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1)}m`;
};

const RelatedScenarioCard = ({ scenario }: { scenario: RelatedScenario }) => {
  const href = scenario.slug
    ? `/s/${scenario.slug}`
    : `/scenarios/${scenario.id}`;
  const creator = scenario.creator_handle
    ? `@${scenario.creator_handle}`
    : scenario.owned_by
    ? "@anonymous"
    : "ManUp";

  return (
    <Link
      to={href}
      prefetch="intent"
      className="block active:opacity-90 rounded-2xl overflow-hidden relative border-1 border-neutral-800"
    >
      <div className="relative h-20 w-full">
        <Image
          src={scenario.image_url}
          alt={`Cover image for ${scenario.name}`}
          height={80}
          width={9999}
          className={cn(
            "object-cover w-full h-full relative z-0",
            scenario.image_position
          )}
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/80 via-black/40 to-transparent flex flex-col justify-center px-4">
          <h3 className="font-bangers text-lg text-white leading-tight drop-shadow-[1px_1px_0.1px_rgba(0,0,0,1)]">
            {scenario.name}
          </h3>
          <div className="text-[10px] font-mono text-white/85 mt-0.5 inline-flex items-center gap-2">
            <span>by {creator}</span>
            {(scenario.play_count ?? 0) > 0 && (
              <>
                <span aria-hidden className="opacity-50">·</span>
                <span className="inline-flex items-center gap-1">
                  <FaFire className="opacity-80" />
                  {formatPlayCount(scenario.play_count ?? 0)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};
